(() => {
  if (customElements.get('template-landing-navbar')) return;

  customElements.define(
    'template-landing-navbar',
    class extends HTMLElement {
      connectedCallback() {
        if (this.controller) return;

        this.top = this.querySelector('[data-navbar-top]');
        if (!this.top) return;

        this.controller = new AbortController();
        const options = { signal: this.controller.signal };

        this.menu = this.top.querySelector('[data-navbar-menu]');
        this.bottom = this.querySelector('[data-navbar-bottom]');
        this.offset = Number(this.dataset.offset) || 260;
        this.productPage = this.dataset.productPage === 'true';
        this.productReady = !this.productPage;
        this.bottomVisible = false;

        if (this.bottom) {
          document.body.append(this.bottom);

          this.space = document.createElement('div');
          this.space.className = 'tlph-bottom__space';
          this.space.setAttribute('aria-hidden', 'true');
          document.body.append(this.space);
        }

        this.measure = () => {
          if (!this.bottom || !this.space) return;

          this.space.style.height =
            Math.ceil(this.bottom.getBoundingClientRect().height) + 'px';
        };

        this.update = () => {
          this.frame = null;
          if (!this.bottom) return;

          const visible =
            window.scrollY > this.offset &&
            !this.menu?.open &&
            this.productReady;

          if (visible === this.bottomVisible) return;

          this.bottomVisible = visible;
          this.bottom.classList.toggle('is-visible', visible);
          this.bottom.inert = !visible;
          this.bottom.setAttribute('aria-hidden', String(!visible));
        };

        this.schedule = () => {
          if (this.frame == null) {
            this.frame = requestAnimationFrame(this.update);
          }
        };

        this.syncProduct = () => {
          this.productFrame = null;

          if (!this.bottom || !this.productPage) return;

          const root = this.productRoot;
          const submit = root?.querySelector('[data-submit]');
          const button = this.bottom.querySelector('[data-navbar-add]');

          this.productReady = Boolean(
            root?.isConnected &&
            root.currentOffer &&
            typeof root.submit === 'function' &&
            submit
          );

          if (!this.productReady) {
            if (button) button.disabled = true;
            this.schedule();
            return;
          }

          const set = (selector, value) => {
            const node = this.bottom.querySelector(selector);

            if (node && node.textContent !== value) {
              node.textContent = value;
            }
          };

          const productTitle =
            root.querySelector('[data-title]')?.textContent.trim() ||
            'Spiruline française';

          set('[data-navbar-title]', productTitle);

          set(
            '[data-navbar-price]',
            root.querySelector('[data-price]')?.textContent.trim() || '—'
          );

          const sourceCompare = root.querySelector('[data-compare]');
          const compare = this.bottom.querySelector('[data-navbar-compare]');

          if (compare) {
            compare.hidden =
              !sourceCompare ||
              sourceCompare.hidden ||
              !sourceCompare.textContent.trim();

            set(
              '[data-navbar-compare]',
              compare.hidden ? '' : sourceCompare.textContent.trim()
            );
          }

          // Le titre produit suit déjà le format sélectionné.
          const identifyFormat = value => {
            const text = String(value || '')
              .normalize('NFD')
              .replace(/[\u0300-\u036f]/g, '')
              .toLowerCase();

            const flakes = /paillet|flake/.test(text);
            const tablets = /comprim|tablet|^tabs?$/.test(text);

            if (flakes && !tablets) return 'paillettes';
            if (tablets && !flakes) return 'comprimes';

            return '';
          };

          const selectedFormat =
            identifyFormat(productTitle) ||
            identifyFormat(root.state?.format);

          let customSrc = '';

          if (selectedFormat === 'paillettes') {
            customSrc = this.bottom.dataset.imagePaillettes || '';
          } else if (selectedFormat === 'comprimes') {
            customSrc = this.bottom.dataset.imageComprimes || '';
          }

          // Sans image personnalisée, conserver la photo du format.
          const media = [...root.querySelectorAll('[data-media]')];

          const first =
            media.find(
              element => element.dataset.format === root.state?.format
            ) ||
            media.find(
              element =>
                selectedFormat &&
                identifyFormat(element.dataset.format) === selectedFormat
            ) ||
            media[0];

          const fallbackSrc =
            first?.querySelector('img')?.getAttribute('src') ||
            first?.querySelector('video')?.poster;

          const src = customSrc || fallbackSrc;
          const image = this.bottom.querySelector('[data-navbar-image]');

          if (image && src) {
            image.removeAttribute('srcset');
            image.removeAttribute('sizes');

            if (image.getAttribute('src') !== src) {
              image.src = src;
            }

            image.hidden = false;
          }

          if (button) {
            button.disabled = submit.disabled || Boolean(root.busy);
            button.setAttribute('aria-busy', String(Boolean(root.busy)));

            set(
              '[data-navbar-add]',
              root.busy ? 'Ajout en cours…' : 'Ajouter au panier'
            );
          }

          const sourceError = root.querySelector('[data-error]');

          const message =
            sourceError && !sourceError.hidden
              ? sourceError.textContent.trim()
              : '';

          set('[data-navbar-error]', message);

          const error = this.bottom.querySelector('[data-navbar-error]');
          if (error) error.hidden = !message;

          this.measure();
          this.schedule();
        };

        this.scheduleProduct = () => {
          if (this.productFrame == null) {
            this.productFrame = requestAnimationFrame(this.syncProduct);
          }
        };

        this.bindProduct = () => {
          if (!this.productPage || !this.bottom || !this.isConnected) return;

          const root = [
            ...document.querySelectorAll('template-landing-product')
          ].find(node => {
            try {
              const config =
                node.config ||
                JSON.parse(
                  node.querySelector('[data-tlpc-config]')?.textContent ||
                    '{}'
                );

              return String(config.id) === this.dataset.productId;
            } catch {
              return false;
            }
          });

          if (root !== this.productRoot) {
            this.productEvents?.abort();
            this.productObserver?.disconnect();
            this.productRoot = root;

            if (root) {
              this.productEvents = new AbortController();

              const productOptions = {
                signal: this.productEvents.signal
              };

              root.addEventListener(
                'change',
                this.scheduleProduct,
                productOptions
              );

              root.addEventListener(
                'input',
                this.scheduleProduct,
                productOptions
              );

              this.productObserver = new MutationObserver(
                this.scheduleProduct
              );

              this.productObserver.observe(root, {
                subtree: true,
                childList: true,
                characterData: true,
                attributes: true,
                attributeFilter: ['hidden', 'disabled', 'src', 'value']
              });
            }
          }

          this.syncProduct();
        };

        this.bottom
          ?.querySelector('[data-navbar-add]')
          ?.addEventListener(
            'click',
            () => {
              this.syncProduct();

              const button = this.bottom.querySelector('[data-navbar-add]');
              const submit = this.productRoot?.querySelector('[data-submit]');
              const form = this.productRoot?.querySelector('[data-form]');

              if (
                !this.productReady ||
                !button ||
                button.disabled ||
                !form ||
                !submit
              ) {
                return;
              }

              form.requestSubmit(submit);
              this.syncProduct();
            },
            options
          );

        document.addEventListener(
          'shopify:section:load',
          this.bindProduct,
          options
        );

        document.addEventListener(
          'shopify:section:unload',
          event => {
            if (
              this.productRoot &&
              event.target.contains(this.productRoot)
            ) {
              this.productEvents?.abort();
              this.productObserver?.disconnect();
              this.productRoot = null;
              this.syncProduct();
            }
          },
          options
        );

        this.bindProduct();

        if (this.productPage) {
          customElements
            .whenDefined('template-landing-product')
            .then(() => {
              if (
                this.isConnected &&
                !this.controller?.signal.aborted
              ) {
                this.bindProduct();
              }
            });
        }

        window.addEventListener('scroll', this.schedule, {
          ...options,
          passive: true
        });

        window.addEventListener('pageshow', this.schedule, options);

        window.addEventListener(
          'resize',
          () => {
            if (window.innerWidth > 1080 && this.menu) {
              this.menu.open = false;
            }

            this.measure();
            this.schedule();
          },
          options
        );

        this.menu?.addEventListener('toggle', this.schedule, options);

        this.menu?.addEventListener(
          'click',
          event => {
            if (event.target.closest('a')) {
              this.menu.open = false;
            }
          },
          options
        );

        document.addEventListener(
          'keydown',
          event => {
            if (event.key === 'Escape' && this.menu?.open) {
              this.menu.open = false;
              this.menu.querySelector('summary').focus();
            }
          },
          options
        );

        document.addEventListener(
          'click',
          event => {
            if (this.menu?.open && !this.menu.contains(event.target)) {
              this.menu.open = false;
            }
          },
          options
        );

        this.top.addEventListener(
          'focusout',
          () => {
            requestAnimationFrame(() => {
              if (
                this.isConnected &&
                this.menu?.open &&
                !this.menu.contains(document.activeElement)
              ) {
                this.menu.open = false;
              }
            });
          },
          options
        );

        this.observer = new ResizeObserver(this.measure);

        if (this.bottom) {
          this.observer.observe(this.bottom);
        }

        this.measure();
        this.update();
      }

      disconnectedCallback() {
        this.controller?.abort();
        this.controller = null;

        this.observer?.disconnect();
        this.productEvents?.abort();
        this.productObserver?.disconnect();
        this.productRoot = null;

        if (this.productFrame != null) {
          cancelAnimationFrame(this.productFrame);
        }
        this.productFrame = null;

        if (this.frame != null) {
          cancelAnimationFrame(this.frame);
        }
        this.frame = null;

        if (this.bottom) {
          this.append(this.bottom);
        }

        this.space?.remove();
      }
    }
  );
})();